import { useMarketPositions } from '@/app/components/VaultsItemPageComponents/hooks/useMarketPositions';
import DataTable, {
  DataTableColumn,
  DataTableDollar,
  DataTableStacked,
  DataTableValue,
} from '@/app/shared/ui/DataTable.tsx';
import { Skeleton } from '@/app/shared/ui/Skeleton';

const desktopGridClassName =
  'min-[960px]:grid min-[960px]:w-full min-[960px]:min-w-0 min-[960px]:grid-cols-[minmax(0,1.4fr)_repeat(6,minmax(0,1fr))] min-[960px]:items-center min-[960px]:gap-2 min-[960px]:px-5 xl:gap-4';

type PositionRow = {
  id: string;
  name: string;
  tranche?: string;
  tokenBalance: string;
  deposited: string;
  currentValue: string;
  apy: string;
  yieldEarned: string;
  terms: string;
};

const POSITIONS_COLUMNS: DataTableColumn<PositionRow>[] = [
  {
    id: 'vault',
    header: 'Vault / Tranche',
    cell: (row) => <DataTableStacked primary={row.name} secondary={row.tranche} />,
  },
  {
    id: 'tokenBalance',
    header: 'Share balance',
    cell: (row) => <DataTableValue value={row.tokenBalance} />,
  },
  {
    id: 'deposited',
    header: 'Deposited',
    cell: (row) => <DataTableDollar value={row.deposited} />,
  },
  {
    id: 'currentValue',
    header: 'Current value',
    cell: (row) => <DataTableDollar value={row.currentValue} />,
  },
  {
    id: 'apy',
    header: 'Current APY',
    cell: (row) => <DataTableValue value={row.apy} />,
  },
  {
    id: 'yieldEarned',
    header: 'Yield earned',
    cell: (row) => <DataTableDollar value={row.yieldEarned} />,
  },
  {
    id: 'terms',
    header: 'Withdrawal terms',
    cell: (row) => <DataTableValue value={row.terms} />,
  },
];

const PortfolioPositionsTable = () => {
  const { positions, updatedAt, isLoading, isConnected } = useMarketPositions();

  return (
    <div className='flex min-w-0 flex-col gap-10'>
      <div className='flex flex-col gap-3'>
        <h2 className='text-base font-normal text-brand-1100'>Positions</h2>
        <div className='flex flex-col gap-3 min-[960px]:flex-row min-[960px]:items-start min-[960px]:justify-between'>
          <p className='text-base leading-6 text-gray-400'>
            Current values are illustrative and remain scoped to their individual vault.
          </p>
          <span className='shrink-0 text-base leading-6 text-gray-400'>{updatedAt}</span>
        </div>
      </div>

      {isLoading ? (
        <div className='flex flex-col gap-3'>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton.Line key={index} loading className='h-[74px] w-full rounded-md'>
              <div className='h-[74px]' />
            </Skeleton.Line>
          ))}
        </div>
      ) : !isConnected ? (
        <p className='text-base text-gray-400'>Connect wallet to see your positions</p>
      ) : (
        <DataTable
          data={positions}
          getRowKey={(row) => row.id}
          desktopGridClassName={desktopGridClassName}
          emptyText='No positions yet'
          pagination={{ desktop: 5, mobile: 2 }}
          columns={POSITIONS_COLUMNS}
        />
      )}
    </div>
  );
};

export default PortfolioPositionsTable;
